import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { PuffLoader } from "react-spinners";
import {
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { Banner, Button, Modal } from "@components";
import { useActionInstalments } from "@api/actionsController";
import { useUsers } from "@api/usersController";
import PaymentRequestDetails from "../../payments/displays/PaymentRequestDetails";
import OwnerBadge from "./OwnerBadge";
import { formatMoney, projectionIdentities, projectionIdentity } from "../common";
import { invoicesRequestedTokenMap } from "@utils";
import PlanInstalmentTimeline, {
  PromiseToPayCard,
} from "./PlanInstalmentTimeline";

const parseDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatSendTime = (date) =>
  format(date, date.getMinutes() === 0 ? "haaa" : "h:mmaaa");

/**
 * A payment plan / promise-to-pay detail modal, keyed purely by an action id so any surface (the
 * calendar, a Today's-tasks card) can open it. Shows the plan header, the per-instalment timeline,
 * and — via the bin — the same in-place cancel confirmation the calendar used. Clicking a sent/paid
 * instalment opens its payment request. `focusInstalment` is the instalment that was clicked, shown
 * in the title (the timeline still highlights the plan's current position).
 */
const PlanDetailModal = ({
  actionId,
  isOpen,
  onClose,
  onCancelAction,
  cancelling,
  focusInstalment = null,
  elevated = false,
  owner = null,
  onOpenPaymentRequest,
  // The calendar grid item's own scheduleKind (parsed off X-ONTIK-SCHEDULE-KIND), passed down by
  // callers that have it (e.g. EventDetails). The instalments REST endpoint's header doesn't carry
  // scheduleKind yet, so this is the only way this modal can currently tell a merchant-scheduled
  // send apart from a promise when opened from the calendar; callers that only have an actionId
  // (Tasks, Requests, the activity feed) fall back to the header's actionType, which still resolves
  // every ScheduledPaymentRequest as a plain PromiseToPay until the BFF adds the field.
  scheduleKindHint = null,
}) => {
  const { data, isError, refetch } = useActionInstalments(actionId, isOpen);
  const { data: users } = useUsers();
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // A payment request opened from an instalment row. Opening it closes this modal and shows the
  // request drawer on its own (rather than stacking on top) — otherwise its own sub-modals, like
  // "view message", end up behind the elevated drawer. "View details" in the drawer brings the
  // plan modal back.
  const [selectedPaymentRequestId, setSelectedPaymentRequestId] = useState(null);
  // When an ancestor owns the request drawer (the smart-note flow renders it at the top level and
  // closes itself), delegate the open to it rather than opening our own stacked drawer.
  const selectPaymentRequest = onOpenPaymentRequest ?? setSelectedPaymentRequestId;
  // "View details" in the drawer fires onViewPlan then the drawer's own close(); this flag lets that
  // close fall through to reopening the plan instead of tearing the whole flow down like the X does.
  const returningToPlan = useRef(false);

  useEffect(() => {
    if (!isOpen) {
      setConfirmingCancel(false);
      setSelectedPaymentRequestId(null);
    }
  }, [isOpen]);

  // Refetch whenever the plan comes back into view — a fresh open, or returning from a payment
  // request drawer where a status may have changed — so the timeline never shows stale sends.
  const visible = isOpen && !selectedPaymentRequestId;
  useEffect(() => {
    if (visible && actionId) refetch();
  }, [visible, actionId, refetch]);

  const header = data?.header;
  const instalments = data?.instalments ?? [];
  // scheduleKind — not actionType alone — discriminates a merchant-scheduled send from a promise
  // to pay (see common.jsx's projectionIdentity); both carry actionType "PromiseToPay". Prefer the
  // header's own scheduleKind once the BFF sends one, falling back to the caller's hint off the
  // calendar grid item.
  const identity =
    projectionIdentity({
      projection: { actionType: header?.actionType, scheduleKind: header?.scheduleKind ?? scheduleKindHint },
    }) ?? projectionIdentities.PromiseToPay;
  const { Icon } = identity;
  // A promise to pay (or its merchant-scheduled counterpart) is a single send, so its header box
  // would just restate the one instalment row below — hide it and let the timeline stand alone.
  const isPromise =
    identity === projectionIdentities.PromiseToPay ||
    identity === projectionIdentities.ScheduledPaymentRequest;

  const customerRef = [header?.customerName, header?.reference && `(${header.reference})`]
    .filter(Boolean)
    .join(" ");
  // A statement/bucket send carries no fixed figure (it resolves live at pay time), so name the
  // bucket ("Overdue invoices") instead of showing "£0".
  const bucketLabel = invoicesRequestedTokenMap[header?.invoicesRequested] ?? null;
  const money = header?.amount != null ? formatMoney(header.amount) : bucketLabel;
  const totalMoney = header?.totalAmount ? formatMoney(header.totalAmount) : bucketLabel;
  const start = parseDate(header?.startsOn);
  const summary = header
    ? [
        [money, header.cadence].filter(Boolean).join(" "),
        header.count > 1 ? `${header.count}x payments` : null,
        start ? `Starting ${format(start, "EEE, dd MMM yyyy")} at ${formatSendTime(start)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;
  const planTitle = header
    ? [identity.heading, totalMoney].filter(Boolean).join(" · ")
    : "";

  const createdAt = parseDate(header?.createdAt);
  const createdBy = header?.createdByUserId
    ? users?.find(
        (u) =>
          String(u.id).toLowerCase() ===
          String(header.createdByUserId).toLowerCase(),
      )?.name
    : null;
  const createdLine = createdAt
    ? [`Created ${format(createdAt, "EEEE, dd MMMM yyyy 'at' HH:mm")}`, createdBy]
        .filter(Boolean)
        .join(" · ")
    : null;

  const instalmentPosition =
    focusInstalment && header?.count > 1
      ? `Instalment ${focusInstalment} of ${header.count}`
      : null;

  // Cancel is a write; on a colleague's read-only calendar no onCancelAction is passed, so the whole
  // affordance is hidden (not just disabled).
  const canCancel = !!actionId && !!onCancelAction;

  const cancelBanner =
    header?.actionType === "PaymentPlan"
      ? "All calendar events & scheduled payment requests associated with this payment plan will be permanently deleted."
      : "The calendar event & scheduled payment request will be permanently deleted.";

  return (
    <>
    <Modal
      isOpen={isOpen && !selectedPaymentRequestId}
      onClose={onClose}
      title={
        confirmingCancel
          ? `Cancel ${identity.label.toLowerCase()}`
          : header
            ? [identity.heading, instalmentPosition].filter(Boolean).join(" · ")
            : "Loading…"
      }
      titleClassName="text-xl"
      size="md"
      elevated={elevated}
      lowered={!elevated}
    >
      <div className="flex flex-col">
        {!header && !isError ? (
          <div className="flex items-center justify-center py-20">
            <PuffLoader color="#F95816" size={64} />
          </div>
        ) : isError ? (
          <div className="p-5">
            <div className="rounded-xl border border-zinc-200 p-4 text-sm text-zinc-500">
              Couldn't load the details for this plan.
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-4 p-5">
              {!confirmingCancel && <OwnerBadge owner={owner} />}
              {(customerRef || header?.email || header?.phone) && (
                <div>
                  {customerRef && (
                    <p className="text-sm font-semibold text-zinc-900">
                      {customerRef}
                    </p>
                  )}
                  {(header?.email || header?.phone) && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-zinc-700">
                      {header.email && (
                        <span className="flex items-center gap-1.5">
                          <EnvelopeIcon className="size-4 shrink-0 text-zinc-400" />
                          {header.email}
                        </span>
                      )}
                      {header.phone && (
                        <span className="flex items-center gap-1.5">
                          <DevicePhoneMobileIcon className="size-4 shrink-0 text-zinc-400" />
                          {header.phone}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* A promise hides this header box normally (it would just restate the single
                  instalment row), but while confirming a cancel that row is hidden — so show the
                  box then, matching how a payment plan keeps its summary during the confirm. */}
              {(!isPromise || confirmingCancel) && (
                <div className="flex items-center gap-3.5 rounded-2xl border border-zinc-200 p-4">
                  <div
                    className={`flex size-10 shrink-0 items-center justify-center rounded-md ${identity.tile}`}
                  >
                    <Icon className="size-5" />
                  </div>
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-semibold text-zinc-900">
                      {planTitle || identity.heading}
                    </p>
                    <p className="text-sm text-zinc-600">
                      {summary ?? identity.heading}
                    </p>
                    {createdLine && (
                      <p className="text-xs text-zinc-400">{createdLine}</p>
                    )}
                  </div>
                  {!confirmingCancel && canCancel && (
                    <button
                      type="button"
                      onClick={() => setConfirmingCancel(true)}
                      title={`Cancel ${identity.label.toLowerCase()}`}
                      className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <span className="sr-only">
                        Cancel {identity.label.toLowerCase()}
                      </span>
                      <TrashIcon className="size-5" />
                    </button>
                  )}
                </div>
              )}

              {!confirmingCancel &&
                (isPromise ? (
                  instalments[0] && (
                    <PromiseToPayCard
                      instalment={instalments[0]}
                      identity={identity}
                      amountLabel={bucketLabel}
                      onSelectPaymentRequest={selectPaymentRequest}
                      onRequestCancel={() => setConfirmingCancel(true)}
                      canCancel={canCancel}
                      cancelLabel={identity.label}
                    />
                  )
                ) : (
                  <PlanInstalmentTimeline
                    instalments={instalments}
                    onSelectPaymentRequest={selectPaymentRequest}
                    focusInstalment={focusInstalment}
                  />
                ))}

              {confirmingCancel && (
                <Banner
                  tone="error"
                  message={cancelBanner}
                  textClassName="text-xs font-semibold text-zinc-800"
                />
              )}
            </div>

            {confirmingCancel && (
              <div className="flex shrink-0 items-center justify-between border-t border-zinc-200 p-4">
                <Button color="secondary" onClick={() => setConfirmingCancel(false)}>
                  Back
                </Button>
                <Button
                  color="danger"
                  onClick={() => onCancelAction(actionId)}
                  submitting={cancelling}
                >
                  Cancel {identity.label.toLowerCase()}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>

    {!onOpenPaymentRequest && (
      <PaymentRequestDetails
        paymentRequestId={selectedPaymentRequestId}
        onChange={refetch}
        onViewPlan={() => {
          returningToPlan.current = true;
          setSelectedPaymentRequestId(null);
        }}
        onClose={() => {
          setSelectedPaymentRequestId(null);
          if (returningToPlan.current) {
            returningToPlan.current = false;
            return;
          }
          onClose();
        }}
        compactActions
      />
    )}
    </>
  );
};

export default PlanDetailModal;
